import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/axios';
import './ContactUs.css';

const Icon = ({ d }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

function ContactUs() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    type: null,
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const response = await axios.post('/api/contact', {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        subject: formData.subject,
        message: formData.message
      });

      if (response.data.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you for contacting us! We will get back to you soon.'
        });
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.error || 'Failed to submit contact form. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-line">
        <div className="max-w-[1560px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link className="flex items-center gap-2.5 font-bold" to="/">
            <span className="w-[30px] h-[30px] rounded-lg bg-gradient-to-br from-navy to-indigo grid place-items-center text-white text-[13px] font-extrabold shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)]">Q</span>
            <span>QuickBook Connector</span>
          </Link>
          <nav className="flex items-center gap-2">
            <Link className="px-3 py-2 text-muted font-medium text-sm rounded-lg hover:text-ink hover:bg-soft" to="/">Home</Link>
            <Link className="px-3 py-2 text-muted font-medium text-sm rounded-lg hover:text-ink hover:bg-soft" to="/terms">Terms</Link>
            <Link className="px-3 py-2 text-muted font-medium text-sm rounded-lg hover:text-ink hover:bg-soft" to="/privacy">Privacy</Link>
            <Link className="px-3 py-2 text-ink font-medium text-sm rounded-lg bg-soft" to="/contact-us">Contact</Link>
          </nav>
        </div>
      </header>

      <div className="contact-container">
        <div className="contact-content">
          <div className="contact-info">
            <h1 className="contact-title">Contact Us</h1>
            <p className="contact-description">
              Have questions or need assistance? We're here to help. Fill out the form below and we'll get back to you as soon as possible.
            </p>

            <div className="contact-details">
              <div className="contact-detail-item">
                <div className="contact-icon">
                  <Icon d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6" />
                </div>
                <div>
                  <h3 className="contact-detail-title">Email</h3>
                  <p className="contact-detail-text">support@xcelerateai-api.com</p>
                </div>
              </div>

              <div className="contact-detail-item">
                <div className="contact-icon">
                  <Icon d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                </div>
                <div>
                  <h3 className="contact-detail-title">Website</h3>
                  <p className="contact-detail-text">https://qbo.xcelerateai-api.com</p>
                </div>
              </div>

              <div className="contact-detail-item">
                <div className="contact-icon">
                  <Icon d="M12 2v20 M2 12h20" />
                </div>
                <div>
                  <h3 className="contact-detail-title">Response Time</h3>
                  <p className="contact-detail-text">Typically within 24-48 hours</p>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-form-container">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="fullName" className="form-label">
                  Full Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  className="form-input"
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-input"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  Phone Number (Optional)
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="form-input"
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="company" className="form-label">
                  Company Name (Optional)
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  className="form-input"
                  placeholder="Enter your company name"
                  value={formData.company}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject" className="form-label">
                  Subject <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="form-input"
                  placeholder="Enter the subject of your inquiry"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  Message <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  className="form-textarea"
                  placeholder="Describe your inquiry or issue in detail"
                  rows="6"
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
                <div className="character-count">{formData.message.length}/2000</div>
              </div>

              {submitStatus.type && (
                <div className={`alert alert-${submitStatus.type}`}>
                  {submitStatus.message}
                </div>
              )}

              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactUs;